import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Segment, BookingData } from '../types/bookingTypes'
import { Travel } from '../types/travelTypes'
import { Passenger } from '../types/passengerTypes'
import { AfterSalesRule as ApiAfterSalesRule, AfterSalesProfile } from '../types/aftersalesTypes'
import {
  fetchAfterSalesProfile as fetchAfterSalesProfileService,
  fetchBookingData as fetchBookingDataService,
} from '../services/journeyService'
import { useErrorStore } from '../stores/errorStore'
import { useMyTravelStore } from '../stores/myTravelStore'

// Equipment interfaces
interface Equipment {
  uuid: string
  product_code: string
  product_type: string
  count: number
  price: {
    amount: number
    currency_code: string
  }
  name: string
  tariff_code: string
  passenger_fares: Array<{
    passenger_id: string
    price?: {
      amount: number
      currency_code: string
    }
    tariff_code?: string
  }>
}

interface SelectedEquipment {
  outbound: Equipment[]
  inbound: Equipment[]
}

interface AfterSalesRule {
  allowed: boolean
  effect: string
  allow_time?: number
}

export const useBookingStore = defineStore('booking', () => {
  const errorStore = useErrorStore()
  const myTravelStore = useMyTravelStore()

  const outboundTravel = ref<Travel | null>(null)
  const inboundTravel = ref<Travel | null>(null)
  const segments = ref<Segment[]>([])
  const passengers = ref<Passenger[]>([])
  const roundTrip = ref<boolean>(false)
  const selectedEquipment = ref<SelectedEquipment>({
    outbound: [],
    inbound: [],
  })

  const bookingEquipment = ref<SelectedEquipment>({
    outbound: [],
    inbound: [],
  })

  const selectedEquipmentTotalPrice = computed(() => {
    const outboundTotal = selectedEquipment.value.outbound.reduce((total, item) => {
      const price = item.price?.amount || 0
      return total + price * item.count
    }, 0)
    const inboundTotal = selectedEquipment.value.inbound.reduce((total, item) => {
      const price = item.price?.amount || 0
      return total + price * item.count
    }, 0)
    return outboundTotal + inboundTotal
  })

  const getEquipmentCount = (direction: 'outbound' | 'inbound', productCode: string): number => {
    const bookingCount =
      bookingEquipment.value[direction].find((e) => e.product_code === productCode)?.count || 0
    const selectedCount =
      selectedEquipment.value[direction].find((e) => e.product_code === productCode)?.count || 0
    return bookingCount + selectedCount
  }

  const getExistingEquipment = (direction: 'outbound' | 'inbound', productCode: string) => {
    const equipment = bookingEquipment.value[direction]
    return equipment.find((e) => e.product_code === productCode)
  }

  const bookingNumber = ref<string | null>(null)
  const bookingData = ref<BookingData | null>(null)
  const afterSalesProfile = ref<AfterSalesProfile | null>(null)
  const afterSalesProfileRules = ref<AfterSalesRule[] | null>(null)
  const isRebooking = ref<boolean>(false)
  const bookingToken = ref<string | null>(null) // New field

  const setIsRebooking = (value: boolean) => {
    isRebooking.value = value
  }

  const setBookingNumber = (number: string) => {
    // Ensure we're setting a valid string value
    if (typeof number !== 'string' || !number) {
      console.warn('Invalid booking number provided:', number)
      return
    }
    bookingNumber.value = number
  }

  const setOutboundTravel = (travel: Travel, routeIndex: number, bundleIndex: number) => {
    const selectedRoute = travel.routes[routeIndex]
    const selectedBundle = selectedRoute.bundles?.[bundleIndex]

    // Create a deep copy of the selected bundle to prevent reference issues
    const bundleCopy = selectedBundle
      ? {
          ...selectedBundle,
          items: selectedBundle.items.map((item) => ({ ...item })),
        }
      : null

    outboundTravel.value = {
      ...travel,
      travel_index: 0,
      routes: [
        {
          ...selectedRoute,
          bundles: bundleCopy ? [bundleCopy] : [],
        },
      ],
    }

    // Clear existing outbound segments before adding new ones
    clearSegmentsByDirection('outbound')

    // Handle each leg of the journey
    if (selectedRoute.legs?.length) {
      selectedRoute.legs.forEach((leg) => {
        const segmentItems =
          bundleCopy?.items
            .filter((item) => item.leg_ids?.includes(leg.id))
            .flatMap((item) =>
              item.passenger_fares.map((fare) => ({
                passenger_id: fare.passenger_id,
                tariff_code: fare.tariff_code,
                item_ref: fare.ref_id,
              })),
            ) || []

        // Format date to YYYY-MM-DD
        const validityDate = leg.service_schedule_date.split('T')[0]

        const segment: Segment = {
          client_ref: leg.id,
          departure_station: leg.departure_station,
          arrival_station: leg.arrival_station,
          direction: 'outbound',
          start_validity_date: validityDate,
          service_name: leg.service_name,
          service_identifier: leg.service_identifier,
          origin: leg.departure_station._u_i_c_station_code,
          destination: leg.arrival_station._u_i_c_station_code,
          validity_intervals: [],
          has_new_main_products: false,
          travel_info: [],
          travel_info_extended: [],
          id: leg.id,
          travel_date: validityDate,
          required_products: [],
          validity_service: leg.service_name,
          tariff_group_code: '',
          booking_journey_segments: [
            {
              service_name: leg.service_name,
            },
          ],
          items: segmentItems,
          additional_tariff_conditions: {
            journey_od: {
              origin: leg.departure_station._u_i_c_station_code,
              destination: leg.arrival_station._u_i_c_station_code,
            },
          },
        }

        addSegment(segment)
      })
    }
  }

  const setInboundTravel = (travel: Travel, routeIndex: number, bundleIndex: number) => {
    const selectedRoute = travel.routes[routeIndex]
    const selectedBundle = selectedRoute.bundles?.[bundleIndex]

    // Create a deep copy of the selected bundle to prevent reference issues
    const bundleCopy = selectedBundle
      ? {
          ...selectedBundle,
          items: selectedBundle.items.map((item) => ({ ...item })),
        }
      : null

    inboundTravel.value = {
      ...travel,
      travel_index: 1,
      routes: [
        {
          ...selectedRoute,
          bundles: bundleCopy ? [bundleCopy] : [],
        },
      ],
    }

    // Clear existing inbound segments before adding new ones
    clearSegmentsByDirection('inbound')

    // Handle each leg of the journey
    if (selectedRoute.legs?.length) {
      selectedRoute.legs.forEach((leg) => {
        const segmentItems =
          bundleCopy?.items
            .filter((item) => item.leg_ids?.includes(leg.id))
            .flatMap((item) =>
              item.passenger_fares.map((fare) => ({
                passenger_id: fare.passenger_id,
                tariff_code: fare.tariff_code,
                item_ref: fare.ref_id,
              })),
            ) || []

        // Format date to YYYY-MM-DD
        const validityDate = leg.service_schedule_date.split('T')[0]

        const segment: Segment = {
          client_ref: leg.id,
          departure_station: leg.departure_station,
          arrival_station: leg.arrival_station,
          direction: 'inbound',
          start_validity_date: validityDate,
          service_name: leg.service_name,
          service_identifier: leg.service_identifier,
          origin: leg.departure_station._u_i_c_station_code,
          destination: leg.arrival_station._u_i_c_station_code,
          validity_intervals: [],
          has_new_main_products: false,
          travel_info: [],
          travel_info_extended: [],
          id: leg.id,
          travel_date: validityDate,
          required_products: [],
          validity_service: leg.service_name,
          tariff_group_code: '',
          booking_journey_segments: [
            {
              service_name: leg.service_name,
            },
          ],
          items: segmentItems,
          additional_tariff_conditions: {
            journey_od: {
              origin: leg.departure_station._u_i_c_station_code,
              destination: leg.arrival_station._u_i_c_station_code,
            },
          },
        }

        addSegment(segment)
      })
    }
  }

  const addSegment = (segment: Segment) => {
    segments.value.push(segment)
  }

  const clearSegmentsByDirection = (direction: 'outbound' | 'inbound') => {
    segments.value = segments.value.filter((segment) => segment.direction !== direction)
  }

  const clearTravels = () => {
    outboundTravel.value = null
    inboundTravel.value = null
    segments.value = []
  }

  const addSegments = (newSegments: Segment[]) => {
    segments.value.push(...newSegments)
  }

  const setPassengers = (passengerList: Passenger[]) => {
    passengers.value = passengerList
  }

  const setRoundTrip = (isRoundTrip: boolean) => {
    roundTrip.value = isRoundTrip
  }

  const addOutboundEquipment = (equipment: Equipment) => {
    // Get the price from passenger fares or main price, ensuring it's in øre
    const priceInOre = equipment.passenger_fares?.[0]?.price?.amount || equipment.price?.amount || 0
    const equipmentToAdd: Equipment = {
      ...equipment,
      count: 1,
      price: {
        amount: priceInOre,
        currency_code: 'NOK',
      },
      name: equipment.name || equipment.product_type,
      tariff_code: equipment.passenger_fares?.[0]?.tariff_code || equipment.tariff_code || '',
      passenger_fares:
        equipment.passenger_fares?.map((pf) => ({
          passenger_id: pf.passenger_id,
          price: {
            amount: pf.price?.amount || priceInOre,
            currency_code: 'NOK',
          },
          tariff_code: pf.tariff_code || equipment.tariff_code || '',
        })) || [],
    }
    selectedEquipment.value.outbound.push(equipmentToAdd)

    // Update MyTravelStore with both booking and selected equipment
    const outboundEquipment = [
      ...bookingEquipment.value.outbound,
      ...selectedEquipment.value.outbound,
    ]
    myTravelStore.updateGroupedEquipment('outbound', outboundEquipment)
  }

  const addInboundEquipment = (equipment: Equipment) => {
    // Get the price from passenger fares or main price, ensuring it's in øre
    const priceInOre = equipment.passenger_fares?.[0]?.price?.amount || equipment.price?.amount || 0
    const equipmentToAdd: Equipment = {
      ...equipment,
      count: 1,
      price: {
        amount: priceInOre,
        currency_code: 'NOK',
      },
      name: equipment.name || equipment.product_type,
      tariff_code: equipment.passenger_fares?.[0]?.tariff_code || equipment.tariff_code || '',
      passenger_fares:
        equipment.passenger_fares?.map((pf) => ({
          passenger_id: pf.passenger_id,
          price: {
            amount: pf.price?.amount || priceInOre,
            currency_code: 'NOK',
          },
          tariff_code: pf.tariff_code || equipment.tariff_code || '',
        })) || [],
    }
    selectedEquipment.value.inbound.push(equipmentToAdd)

    // Update MyTravelStore with both booking and selected equipment
    const inboundEquipment = [...bookingEquipment.value.inbound, ...selectedEquipment.value.inbound]
    myTravelStore.updateGroupedEquipment('inbound', inboundEquipment)
  }

  const removeOutboundEquipment = (equipment: Equipment) => {
    const index = selectedEquipment.value.outbound.findIndex(
      (e) => e.product_code === equipment.product_code,
    )
    if (index !== -1) {
      const item = selectedEquipment.value.outbound[index]
      if (item.count > 1) {
        item.count--
      } else {
        selectedEquipment.value.outbound.splice(index, 1)
      }

      // Update MyTravelStore with both booking and selected equipment
      const outboundEquipment = [
        ...bookingEquipment.value.outbound,
        ...selectedEquipment.value.outbound,
      ]
      myTravelStore.updateGroupedEquipment('outbound', outboundEquipment)
    }
  }

  const removeInboundEquipment = (equipment: Equipment) => {
    const index = selectedEquipment.value.inbound.findIndex(
      (e) => e.product_code === equipment.product_code,
    )
    if (index !== -1) {
      const item = selectedEquipment.value.inbound[index]
      if (item.count > 1) {
        item.count--
      } else {
        selectedEquipment.value.inbound.splice(index, 1)
      }

      // Update MyTravelStore with both booking and selected equipment
      const inboundEquipment = [
        ...bookingEquipment.value.inbound,
        ...selectedEquipment.value.inbound,
      ]
      myTravelStore.updateGroupedEquipment('inbound', inboundEquipment)
    }
  }

  const setBookingData = (data: BookingData) => {
    console.log('[DEBUG] bookingStore setBookingData - Received data:', {
      hasOutboundSegments: !!data.outbound_booking_tariff_segments?.length,
      hasInboundSegments: !!data.inbound_booking_tariff_segments?.length,
      hasPassengers: !!data.passengers?.length,
      passengerCount: data.passengers?.length || 0,
      bookingNumber: data.bookingNumber,
      hasCustomer: !!data.customer,
    })

    bookingData.value = data
    bookingNumber.value = data.bookingNumber || bookingNumber.value
    bookingToken.value = data.bookingToken || bookingToken.value

    // Set passengers
    if (data.passengers) {
      console.log('[DEBUG] bookingStore setBookingData - Setting passengers:', {
        count: data.passengers.length,
        passengers: data.passengers.map((p) => ({ id: p.id, type: p.type })),
      })
      passengers.value = data.passengers
    }

    // Set outboundTravel from outbound_booking_tariff_segments
    if (data.outbound_booking_tariff_segments?.length) {
      const legs = data.outbound_booking_tariff_segments.map((segment) => ({
        id: segment.client_ref,
        departure_station: segment.departure_station,
        arrival_station: segment.arrival_station,
        departure_timestamp: segment.start_validity_date,
        arrival_timestamp: segment.end_validity_date || segment.start_validity_date,
        service_name: segment.service_name,
        service_identifier: segment.service_identifier,
        _u_i_c_station_code: segment.departure_station._u_i_c_station_code,
      }))

      outboundTravel.value = {
        travel_index: 0,
        routes: [
          {
            legs,
            bundles: [],
          },
        ],
      }
      console.log('[DEBUG] bookingStore setBookingData - Set outboundTravel:', {
        legCount: legs.length,
        firstLeg: legs[0]
          ? {
              departure: legs[0].departure_station.name,
              arrival: legs[0].arrival_station.name,
              date: legs[0].departure_timestamp,
            }
          : null,
      })
    } else {
      outboundTravel.value = null
      console.log(
        '[DEBUG] bookingStore setBookingData - No outbound segments, outboundTravel set to null',
      )
    }

    // Set inboundTravel from inbound_booking_tariff_segments
    if (data.inbound_booking_tariff_segments?.length) {
      const legs = data.inbound_booking_tariff_segments.map((segment) => ({
        id: segment.client_ref,
        departure_station: segment.departure_station,
        arrival_station: segment.arrival_station,
        departure_timestamp: segment.start_validity_date,
        arrival_timestamp: segment.end_validity_date || segment.start_validity_date,
        service_name: segment.service_name,
        service_identifier: segment.service_identifier,
        _u_i_c_station_code: segment.departure_station._u_i_c_station_code,
      }))

      inboundTravel.value = {
        travel_index: 1,
        routes: [
          {
            legs,
            bundles: [],
          },
        ],
      }
      console.log('[DEBUG] bookingStore setBookingData - Set inboundTravel:', {
        legCount: legs.length,
        firstLeg: legs[0]
          ? {
              departure: legs[0].departure_station.name,
              arrival: legs[0].arrival_station.name,
              date: legs[0].departure_timestamp,
            }
          : null,
      })
    } else {
      inboundTravel.value = null
      console.log(
        '[DEBUG] bookingStore setBookingData - No inbound segments, inboundTravel set to null',
      )
    }

    // Reset equipment before initializing
    selectedEquipment.value = { outbound: [], inbound: [] }
    bookingEquipment.value = { outbound: [], inbound: [] }

    // Initialize booking equipment
    if (data.outbound_booking_tariff_segments) {
      const productsByUuid = new Map<string, any>()
      data.outbound_booking_tariff_segments.forEach((segment) => {
        segment.additional_products?.forEach((product) => {
          if (!product.cancelled && product.type !== 'SF' && !productsByUuid.has(product.uuid)) {
            const priceInOre = (product.price_unconverted || product.price || 0) * 100
            productsByUuid.set(product.uuid, {
              uuid: product.uuid,
              product_code: product.code,
              product_type: product.type || '',
              count: 1,
              price: { amount: priceInOre, currency_code: 'NOK' },
              name: product.name || product.type || '',
              tariff_code: product.tariff_code || '',
              passenger_fares: [],
            })
          }
        })
      })

      const groupedOutbound = new Map<string, Equipment>()
      Array.from(productsByUuid.values()).forEach((product) => {
        if (groupedOutbound.has(product.product_code)) {
          const existing = groupedOutbound.get(product.product_code)!
          existing.count += 1
        } else {
          groupedOutbound.set(product.product_code, { ...product })
        }
      })
      bookingEquipment.value.outbound = Array.from(groupedOutbound.values())
    }

    if (data.inbound_booking_tariff_segments) {
      const productsByUuid = new Map<string, any>()
      data.inbound_booking_tariff_segments.forEach((segment) => {
        segment.additional_products?.forEach((product) => {
          if (!product.cancelled && product.type !== 'SF' && !productsByUuid.has(product.uuid)) {
            const priceInOre = (product.price_unconverted || product.price || 0) * 100
            productsByUuid.set(product.uuid, {
              uuid: product.uuid,
              product_code: product.code,
              product_type: product.type || '',
              count: 1,
              price: { amount: priceInOre, currency_code: 'NOK' },
              name: product.name || product.type || '',
              tariff_code: product.tariff_code || '',
              passenger_fares: [],
            })
          }
        })
      })

      const groupedInbound = new Map<string, Equipment>()
      Array.from(productsByUuid.values()).forEach((product) => {
        if (groupedInbound.has(product.product_code)) {
          const existing = groupedInbound.get(product.product_code)!
          existing.count += 1
        } else {
          groupedInbound.set(product.product_code, { ...product })
        }
      })
      bookingEquipment.value.inbound = Array.from(groupedInbound.values())
    }

    // Update MyTravelStore
    const outboundEquipment = [
      ...bookingEquipment.value.outbound,
      ...selectedEquipment.value.outbound,
    ]
    const inboundEquipment = [...bookingEquipment.value.inbound, ...selectedEquipment.value.inbound]
    myTravelStore.updateGroupedEquipment('outbound', outboundEquipment)
    myTravelStore.updateGroupedEquipment('inbound', inboundEquipment)
  }

  const setBooking = (data: any) => {
    bookingData.value = data
  }

  const fetchAfterSalesProfile = async (bookingNum: string) => {
    try {
      const response = await fetchAfterSalesProfileService(bookingNum)
      const profile = response?.aftersales_profiles?.[0]
      if (profile) {
        afterSalesProfile.value = {
          id: profile.id,
          rebook_ruleset: profile.rebook_ruleset,
          cancel_ruleset: profile.cancel_ruleset,
        }
        // Convert API rules to our internal format for rebooking
        afterSalesProfileRules.value =
          profile.rebook_ruleset?.rules?.map(
            (rule: ApiAfterSalesRule): AfterSalesRule => ({
              allowed: rule.allowed,
              effect: rule.effect || '',
              allow_time: rule.allow_time,
            }),
          ) || null
      }
    } catch (error) {
      console.error('Error fetching after sales profile:', error)
      errorStore.setError('Error fetching after sales profile')
      return null
    }
  }

  const fetchBookingData = async (bookingNum: string) => {
    try {
      const response = await fetchBookingDataService(bookingNum)
      if (response?.data?.booking) {
        setBookingData(response.data.booking)
      } else {
        console.error('Invalid booking data structure:', response)
        throw new Error('Invalid booking data structure')
      }
    } catch (error) {
      console.error('Error fetching booking data:', error)
      throw error
    }
  }

  const canRebookJourney = (direction: 'outbound' | 'inbound'): boolean => {
    if (!afterSalesProfile.value?.rebook_ruleset?.rules) return false

    const segments =
      direction === 'outbound'
        ? bookingData.value?.outbound_booking_tariff_segments
        : bookingData.value?.inbound_booking_tariff_segments

    if (!segments?.length) return false

    // Check if all segments can be rebooked
    return segments.every(() => {
      const hasRebookingRule = afterSalesProfile.value?.rebook_ruleset?.rules.some(
        (rule: ApiAfterSalesRule) => rule.allowed && rule.effect === 'ALLOW',
      )

      if (!hasRebookingRule) return false

      // Get the rebooking rule
      const rebookingRule = afterSalesProfile.value?.rebook_ruleset?.rules.find(
        (rule: ApiAfterSalesRule) => rule.allowed && rule.effect === 'ALLOW',
      )

      if (!rebookingRule) return false

      // Check if current date is within validity period
      if (!rebookingRule.allow_time) return false
      const now = new Date()
      const validUntil = new Date()
      validUntil.setDate(validUntil.getDate() + (rebookingRule.allow_time || 0))
      return now <= validUntil
    })
  }

  const getRebookingRestrictions = () => {
    if (!afterSalesProfile.value?.rebook_ruleset?.rules) return null

    const rebookingRule = afterSalesProfile.value.rebook_ruleset.rules.find(
      (rule: ApiAfterSalesRule) => rule.allowed && rule.effect === 'ALLOW',
    )

    if (!rebookingRule) return null

    if (!rebookingRule.allow_time) return null
    const now = new Date()
    const validUntil = new Date()
    validUntil.setDate(validUntil.getDate() + (rebookingRule.allow_time || 0))
    return {
      validUntil,
      isWithinValidityPeriod: now <= validUntil,
    }
  }

  const transformSegmentForApi = (segment: Segment) => {
    return {
      service_identifier: segment.service_identifier,
      service_name: segment.service_name,
      origin: segment.origin,
      destination: segment.destination,
      direction: segment.direction,
      start_validity_date: segment.start_validity_date,
      items: segment.items.map((item) => ({
        passenger_id: item.passenger_id,
        tariff_code: item.tariff_code,
        item_ref: item.item_ref || item.client_ref || '',
      })),
    }
  }

  const getBookingRequestData = () => {
    return {
      currency: 'NOK',
      passengers: passengers.value.map((p) => ({
        id: p.id,
        type: p.type,
        discount_cards: p.discount_cards || [],
      })),
      segments: segments.value.map(transformSegmentForApi),
    }
  }

  const $reset = () => {
    const existingOutboundEquipment = selectedEquipment.value.outbound
    const existingInboundEquipment = selectedEquipment.value.inbound

    // Clear state
    outboundTravel.value = null
    inboundTravel.value = null
    segments.value = []
    passengers.value = []
    roundTrip.value = false
    selectedEquipment.value = {
      outbound: [],
      inbound: [],
    }
    bookingEquipment.value = {
      outbound: [],
      inbound: [],
    }
    bookingNumber.value = null
    bookingData.value = null
    afterSalesProfile.value = null
    afterSalesProfileRules.value = null
    isRebooking.value = false

    // Restore equipment state to myTravelStore
    myTravelStore.updateGroupedEquipment('outbound', existingOutboundEquipment)
    myTravelStore.updateGroupedEquipment('inbound', existingInboundEquipment)
  }

  return {
    outboundTravel,
    inboundTravel,
    segments,
    passengers,
    roundTrip,
    bookingNumber,
    bookingData,
    afterSalesProfile,
    afterSalesProfileRules,
    selectedEquipment,
    bookingEquipment,
    selectedEquipmentTotalPrice,
    isRebooking,
    bookingToken,
    setBookingNumber,
    setOutboundTravel,
    setInboundTravel,
    addSegment,
    addSegments,
    clearSegmentsByDirection,
    clearTravels,
    setPassengers,
    setRoundTrip,
    addOutboundEquipment,
    addInboundEquipment,
    removeOutboundEquipment,
    removeInboundEquipment,
    setBookingData,
    setBooking,
    fetchAfterSalesProfile,
    fetchBookingData,
    canRebookJourney,
    getRebookingRestrictions,
    transformSegmentForApi,
    getBookingRequestData,
    getEquipmentCount,
    getExistingEquipment,
    setIsRebooking,
    $reset,
  }
})
